import { useEffect, useState, createContext, useContext, useCallback } from "react";
import { api } from "@/lib/api";
import { AlertTriangle } from "lucide-react";

/**
 * Contexte global qui charge une fois la liste des litiges actifs et indexe
 * { byThirdParty: {id: [disputes]}, byOrder: {id: [disputes]} }. Tous les composants
 * `DisputeBadge` qui ont besoin d'afficher le ⚠️ partagent ce cache.
 */
const Ctx = createContext({ byThirdParty: {}, byOrder: {}, refresh: () => {}, loading: false });

export const DisputeBadgeProvider = ({ children }) => {
  const [byThirdParty, setByThirdParty] = useState({});
  const [byOrder, setByOrder] = useState({});
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.getActiveDisputesByThirdParty();
      setByThirdParty(data?.by_third_party || {});
      setByOrder(data?.by_order || {});
    } catch (_e) {
      // silencieux : non bloquant
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 60_000); // refresh chaque minute
    return () => clearInterval(t);
  }, [refresh]);

  return <Ctx.Provider value={{ byThirdParty, byOrder, refresh, loading }}>{children}</Ctx.Provider>;
};

export const useDisputeBadges = () => useContext(Ctx);

const SEVERITY_RANK = { critical: 4, high: 3, medium: 2, low: 1 };

/**
 * Affiche le triangle ⚠️ rouge si le tiers (ou la commande) a au moins un litige actif.
 * Usage : <DisputeBadge thirdPartyId={client.key} />  ou  <DisputeBadge orderId={order.id} />
 */
export const DisputeBadge = ({ thirdPartyId, orderId, size = 14, className = "" }) => {
  const { byThirdParty, byOrder } = useDisputeBadges();
  const list = (orderId ? byOrder[orderId] : (thirdPartyId ? byThirdParty[thirdPartyId] : null)) || [];
  if (list.length === 0) return null;
  // Pick worst severity
  const worst = list.reduce((acc, d) =>
    (SEVERITY_RANK[d.severity] || 0) > (SEVERITY_RANK[acc.severity] || 0) ? d : acc, list[0]);
  const tip = list.length === 1
    ? `Litige en cours — ${worst.reason || worst.status}`
    : `${list.length} litiges en cours — pire : ${worst.severity}`;
  return (
    <span
      className={`inline-flex items-center text-red-600 ${className}`}
      title={tip}
      data-testid={`dispute-badge${orderId ? `-order-${orderId}` : thirdPartyId ? `-third-${thirdPartyId}` : ""}`}
    >
      <AlertTriangle size={size} fill="currentColor" className="opacity-80" />
    </span>
  );
};

export default DisputeBadge;
